// Phone numbers, as far as this package goes.
//
// Section 3.6 of the field library: stored as entered, plus a normalised form
// where parsing succeeds. Required numbers now block when the national length
// is wrong: a half-typed US number is not a number somebody can be called on.
// Optional numbers still warn, so a crew member can carry a foreign oddity
// through.
//
// No library. A full E.164 parser carries a metadata table of every numbering
// plan on earth. This does the countries Nova runs shows in.

/** Trunk-zero countries Nova has activations in or is asked about. */
const DIALLING_CODES: Readonly<Record<string, string>> = {
  GB: "44",
  IE: "353",
  FR: "33",
  DE: "49",
  NL: "31",
  BE: "32",
  ES: "34",
  IT: "39",
  SE: "46",
  DK: "45",
  NO: "47",
  AU: "61",
  NZ: "64",
  ZA: "27",
  AE: "971",
};

/** Countries with no trunk prefix, where the national number is dialled whole. */
const NO_TRUNK_PREFIX: Readonly<Record<string, string>> = {
  US: "1",
  CA: "1",
};

/**
 * National significant number length, after a trunk zero is stripped.
 *
 * US and CA are 10, as asked. GB is 10 after the leading 0. A hard "10
 * everywhere" would refuse a French 9-digit mobile.
 */
const NATIONAL_LENGTH: Readonly<Record<string, { readonly min: number; readonly max: number }>> = {
  US: { min: 10, max: 10 },
  CA: { min: 10, max: 10 },
  GB: { min: 10, max: 10 },
  IE: { min: 9, max: 9 },
  FR: { min: 9, max: 9 },
  DE: { min: 10, max: 11 },
  NL: { min: 9, max: 9 },
  BE: { min: 8, max: 9 },
  ES: { min: 9, max: 9 },
  IT: { min: 9, max: 10 },
  SE: { min: 7, max: 10 },
  DK: { min: 8, max: 8 },
  NO: { min: 8, max: 8 },
  AU: { min: 9, max: 9 },
  NZ: { min: 8, max: 10 },
  ZA: { min: 9, max: 9 },
  AE: { min: 8, max: 9 },
};

export const PHONE_COUNTRIES: readonly { readonly code: string; readonly label: string }[] = [
  { code: "GB", label: "GB +44" },
  { code: "US", label: "US +1" },
  { code: "CA", label: "CA +1" },
  { code: "IE", label: "IE +353" },
  { code: "FR", label: "FR +33" },
  { code: "DE", label: "DE +49" },
  { code: "NL", label: "NL +31" },
  { code: "BE", label: "BE +32" },
  { code: "ES", label: "ES +34" },
  { code: "IT", label: "IT +39" },
  { code: "SE", label: "SE +46" },
  { code: "DK", label: "DK +45" },
  { code: "NO", label: "NO +47" },
  { code: "AU", label: "AU +61" },
  { code: "NZ", label: "NZ +64" },
  { code: "ZA", label: "ZA +27" },
  { code: "AE", label: "AE +971" },
];

const strip = (entered: string): string => entered.replace(/[\s().\u2010-\u2015-]/g, "");

export function callingCode(country: string): string | null {
  const code = country.toUpperCase();
  return DIALLING_CODES[code] ?? NO_TRUNK_PREFIX[code] ?? null;
}

export function hasTrunkZero(country: string): boolean {
  return DIALLING_CODES[country.toUpperCase()] !== undefined;
}

/**
 * Digits the person typed for the national number, trunk zero stripped.
 *
 * A stored value may be `GB:07700900123` (country + national) or a bare number
 * from an older bundle. The prefix is how the country selector survives a
 * remount without becoming a second field.
 */
export function phoneCountryOf(entered: string, fallback: string): string {
  const match = /^([A-Za-z]{2}):/.exec(entered);
  const code = match?.[1];
  if (code !== undefined) return code.toUpperCase();
  return fallback.toUpperCase();
}

export function nationalDigits(entered: string): string {
  const body = entered.includes(":") ? entered.slice(entered.indexOf(":") + 1) : entered;
  return body.replace(/\D/g, "");
}

export function encodePhoneValue(country: string, digits: string): string {
  const national = digits.replace(/\D/g, "");
  if (national === "") return "";
  return `${country.toUpperCase()}:${national}`;
}

export function nationalLengthOf(country: string): { readonly min: number; readonly max: number } {
  return NATIONAL_LENGTH[country.toUpperCase()] ?? { min: 7, max: 15 };
}

/**
 * Significant national digits, trunk zero removed for trunk-zero countries.
 */
export function significantNationalDigits(entered: string, country: string): string {
  const digits = nationalDigits(entered);
  if (hasTrunkZero(country) && digits.startsWith("0")) return digits.slice(1);
  return digits;
}

export function nationalLengthOk(entered: string, country: string): boolean {
  const significant = significantNationalDigits(entered, country);
  const { min, max } = nationalLengthOf(country);
  return significant.length >= min && significant.length <= max;
}

/**
 * Typed past what this country uses. Not a refusal: a French 9 and a German 11
 * are both real, and a hard "10 everywhere" would call one of them wrong.
 * Shown live once the box is already longer than the country's maximum, because
 * at that point the person is not mid-number, they have gone past it.
 */
export function phoneLooksTooLong(entered: string, country: string): boolean {
  if (!entered.includes(":")) return false;
  return significantNationalDigits(entered, country).length > nationalLengthOf(country).max;
}

export const PHONE_TOO_LONG = "This number looks too long. Check it.";

/**
 * Shape only, and generous. Used for optional numbers that still warn.
 */
export function looksLikePhone(entered: string): boolean {
  if (entered.includes(":")) return nationalLengthOk(entered, phoneCountryOf(entered, "GB"));
  const bare = strip(entered);
  if (!/^\+?\d+$/.test(bare)) return false;
  const digits = bare.replace(/^\+/, "").length;
  return digits >= 7 && digits <= 15;
}

export function normalisePhone(entered: string, defaultCountry?: string): string | null {
  if (entered.includes(":")) {
    const country = phoneCountryOf(entered, defaultCountry ?? "GB");
    const significant = significantNationalDigits(entered, country);
    const code = callingCode(country);
    if (!code) return null;
    const { min, max } = nationalLengthOf(country);
    if (significant.length < min || significant.length > max) return null;
    return `+${code}${significant}`;
  }

  const bare = strip(entered);
  if (!/^\+?\d+$/.test(bare)) return null;

  if (bare.startsWith("+")) {
    const digits = bare.slice(1);
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }

  const country = defaultCountry?.toUpperCase();
  if (!country) return null;

  const trunkCode = DIALLING_CODES[country];
  if (trunkCode) {
    if (!bare.startsWith("0")) return null;
    const national = bare.slice(1);
    return national.length >= 7 ? `+${trunkCode}${national}` : null;
  }

  const flatCode = NO_TRUNK_PREFIX[country];
  if (flatCode) {
    return bare.length === 10 ? `+${flatCode}${bare}` : null;
  }

  return null;
}
